import React, { useMemo } from 'react';
import { CELERITY, SUSTENANCE, SORCERY, MIGHT, FORTITUDE } from './constants';
import { computeFinalStats, getBaseStats, HEROES, ARTIFACTS, EQUIP_QUALITIES, getWeapon, getBoots, getChest, getHelmet, getBranchName, getUnionCount, floorAll } from './utils';
import './StatCalculator.scss';

const STAT_KEYS = [
  "HP",
  "ATK",
  "DEF",
  "CRIT",
  "DODGE",
  "MSPD",
  "MR",
  "PR",
  "LL",
  "ACC",
];

const ASCENSIONS = [
  { name: 'Common', value: 1 },
  { name: 'Rare', value: 2 },
  { name: 'Rare+', value: 3 },
  { name: 'Epic', value: 4 },
  { name: 'Epic+', value: 5 },
  { name: 'Legendary', value: 6 },
  { name: 'Legendary+', value: 7 },
  { name: 'Mythic', value: 8 },
  { name: 'Mythic+', value: 9 },
  { name: 'Ascended 0 Star', value: 10 },
  { name: 'Ascended 1 Star', value: 11 },
  { name: 'Ascended 2 Star', value: 12 },
  { name: 'Ascended 3 Star', value: 13 },
  { name: 'Ascended 4 Star', value: 14 },
  { name: 'Ascended 5 Star', value: 15 },
];

const realLevel = level => level >= 240 ? Math.round((level - 240) * 10 + 240) : level;

export default ({
  treeLevel,
  celerityLevel,
  mightLevel,
  fortitudeLevel,
  sorceryLevel,
  sustenanceLevel,
  ascension,
  updateAscension,
  hasSignature,
  updateHasSignature,
  signature,
  setSignature,
  updateSignature,
  artifactId,
  updateArtifactId,
  artifactLevel,
  updateArtifactLevel,
  unionLevel,
  updateUnionLevel,
  
  weaponLevel,
  updateWeaponLevel,
  helmetLevel,
  updateHelmetLevel,
  chestLevel,
  updateChestLevel,
  bootsLevel,
  updateBootsLevel,
  
  weaponQuality,
  updateWeaponQuality,
  helmetQuality,
  updateHelmetQuality,
  chestQuality,
  updateChestQuality,
  bootsQuality,
  updateBootsQuality,
  
  weaponFaction,
  updateWeaponFaction,
  helmetFaction,
  updateHelmetFaction,
  chestFaction,
  updateChestFaction,
  bootsFaction,
  updateBootsFaction,
  
  crystal,
  setCrystal,
  updateCrystal,
  heroSelection,
  updateHeroSelection,
  maxSignature,
}) => {

  const branchName = useMemo(() => getBranchName(heroSelection), [heroSelection]);
  let branchLevel = 0;
  switch(branchName){
    case CELERITY:
      branchLevel = celerityLevel;
      break;
    case SUSTENANCE:
      branchLevel = sustenanceLevel;
      break;
    case MIGHT:
      branchLevel = mightLevel;
      break;
    case SORCERY:
      branchLevel = sorceryLevel;
      break;
    case FORTITUDE:
      branchLevel = fortitudeLevel;
      break;
    default:
      branchLevel = 0;
      break;
  }

  const level = realLevel(crystal);

  const weapon = useMemo(() => ({
    level: weaponLevel,
    factionBonus: weaponFaction,
    id: getWeapon(heroSelection, weaponQuality).TID,
  }), [weaponLevel, weaponFaction, weaponQuality, heroSelection]);

  const helmet = useMemo(() => ({
    level: helmetLevel,
    factionBonus: helmetFaction,
    id: getHelmet(heroSelection, helmetQuality).TID,
  }), [helmetLevel, helmetFaction, helmetQuality, heroSelection]);

  const chest = useMemo(() => ({
    level: chestLevel,
    factionBonus: chestFaction,
    id: getChest(heroSelection, chestQuality).TID,
  }), [chestLevel, chestFaction, chestQuality, heroSelection]);

  const boots = useMemo(() => ({
    level: bootsLevel,
    factionBonus: bootsFaction,
    id: getBoots(heroSelection, bootsQuality).TID,
  }), [bootsLevel, bootsFaction, bootsQuality, heroSelection]);

  const hasUnion = useMemo(() => getUnionCount(heroSelection) > 0, [heroSelection]);

  const baseStats = floorAll(getBaseStats(({ id: heroSelection, quality: ascension, level })));

  const finalStats = floorAll(computeFinalStats({
    heroId: heroSelection,
    ascension,
    crystalLevel: level,
    treeLevel,
    branchLevel,
    signatureLevel: hasSignature ? signature : -1,
    unionLevel,
    artifactId,
    artifactLevel,
    equipment: [
      weapon,
      helmet,
      chest,
      boots
    ],
  }));

  const finalStatsWithoutBranch = floorAll(computeFinalStats({
    heroId: heroSelection,
    ascension,
    crystalLevel: level,
    treeLevel,
    branchLevel: 0,
    signatureLevel: hasSignature ? signature : -1,
    unionLevel,
    artifactId,
    artifactLevel,
    equipment: [
      weapon,
      helmet,
      chest,
      boots
    ],
  }));

  return (
    <div id="stat-calculator">
      <div id="stat-calculator-controls">
        <div id="hero-controls">
          <div id="base-controls">
          <div id="hero-select">
            <select id="hero-select-input" value={heroSelection} onChange={updateHeroSelection}>
              {HEROES.map(hero => <option key={hero.ID} value={hero.ID}>{
              hero.DisplayName && hero.DisplayName !== ' '
                ? hero.DisplayName
                : `Unknown Hero ${hero.ID}`}</option>)
              }
            </select>
          </div>
          <div id="other-base-stuff">
          <span id="ascension">
            Ascension: 
            <select value={ascension} onChange={updateAscension}>
              {ASCENSIONS.map(ascension => <option key={`ascension-${ascension.name}`} value={ascension.value}>{ascension.name}</option>)}
            </select>
          </span>
          <span id="level">
              Level:
              <input type="number" value={crystal} min={1} max={604.9} step={level >= 240 ? 0.1 : 1} onChange={updateCrystal} inputMode="decimal" pattern="[0-9.]*" />
          </span>
          </div>
          </div>
          <div id="sig-controls">
          <span id="signature">
            Signature Unlocked:
            <input type="checkbox" checked={hasSignature} onChange={updateHasSignature} />
            Signature
            <input type="number" value={signature} disabled={!hasSignature} min={0} max={maxSignature} onChange={updateSignature} inputMode="numeric" pattern="[0-9]*" />
          </span>
          </div>
          <div id="artifact-controls">
          <span id="Artifact">
            Artifact: 
            <select value={artifactId} onChange={updateArtifactId}>
              <option value={0}>None</option>
              {ARTIFACTS.map(artifact => <option key={`artifact-${artifact.ID}`} value={artifact.ID}>{artifact.Name}</option>)}
            </select>
            Stars:
            <input type="number" value={artifactLevel} disabled={parseInt(artifactId) === 0} min="0" max="5" onChange={updateArtifactLevel} inputMode="numeric" pattern="[0-9]*" />
          </span>
          </div>
          <div id="union-controls">
          <span id="union">
            {hasUnion
              ? (
              <>
                Union Level:
                <input type="number" value={unionLevel} disabled={!hasUnion} min="0" max="3" onChange={updateUnionLevel} inputMode="numeric" pattern="[0-9]*" />
              </>
            ) : (
              <>No Union</>
            )}
          </span>
          </div>
          <div id="weapon controls">
          <span id="weapon">
            Weapon: 
            <select value={weaponQuality} onChange={updateWeaponQuality}>
              <option value={0}>None</option>
              {EQUIP_QUALITIES.map(quality => <option key={`weapon-${quality.Name}`} value={quality.Quality}>{quality.Name}</option>)}
            </select>
            Stars: <input type="number" value={weaponLevel} disabled={parseInt(weaponQuality) === 0} min="0" max="5" onChange={updateWeaponLevel} inputMode="numeric" pattern="[0-9]*" />
            Faction Bonus?
            <input type="checkbox" checked={weaponFaction} disabled={parseInt(weaponQuality) === 0} onChange={updateWeaponFaction} />
          </span>
          </div>
          <div id="helmet-controls">
          <span id="helmet">
            Helmet: 
            <select value={helmetQuality} onChange={updateHelmetQuality}>
              <option value={0}>None</option>
              {EQUIP_QUALITIES.map(quality => <option key={`helmet-${quality.Name}`} value={quality.Quality}>{quality.Name}</option>)}
            </select>
            Stars: <input type="number" value={helmetLevel} disabled={parseInt(helmetQuality) === 0} min="0" max="5" onChange={updateHelmetLevel} inputMode="numeric" pattern="[0-9]*" />
            Faction Bonus?
            <input type="checkbox" checked={helmetFaction} disabled={parseInt(helmetQuality) === 0} onChange={updateHelmetFaction} />
          </span>
          </div>
          <div id="chest-controls">
          <span id="chest">
            Chest: 
            <select value={chestQuality} onChange={updateChestQuality}>
              <option value={0}>None</option>
              {EQUIP_QUALITIES.map(quality => <option key={`chest-${quality.Name}`} value={quality.Quality}>{quality.Name}</option>)}
            </select>
            Stars: <input type="number" value={chestLevel} disabled={parseInt(chestQuality) === 0} min="0" max="5" onChange={updateChestLevel} inputMode="numeric" pattern="[0-9]*" />
            Faction Bonus?
            <input type="checkbox" checked={chestFaction} disabled={parseInt(chestQuality) === 0} onChange={updateChestFaction} />
          </span>
          </div>
          <div id="boots-controls">
          <span id="boots">
            Boots: 
            <select value={bootsQuality} onChange={updateBootsQuality}>
              <option value={0}>None</option>
              {EQUIP_QUALITIES.map(quality => <option key={`boots-${quality.Name}`} value={quality.Quality}>{quality.Name}</option>)}
            </select>
            Stars: <input type="number" value={bootsLevel} disabled={parseInt(bootsQuality) === 0} min="0" max="5" onChange={updateBootsLevel} inputMode="numeric" pattern="[0-9]*" />
            Faction Bonus?
            <input type="checkbox" checked={bootsFaction} disabled={parseInt(bootsQuality) === 0 } onChange={updateBootsFaction} />
          </span>
          </div>
        </div>
      </div>
      <div id="final-stats">
        {STAT_KEYS.map(key => (
          <div key={key} className="stat-row">
            <span className="stat-key">{key}</span>
            <div className="stat-values">
              <span className="final-stat">Final Value: {finalStats[key]}</span>
              <span className="without-branch">
                Without branch: {finalStatsWithoutBranch[key]}
                {' '}({(((finalStats[key] / finalStatsWithoutBranch[key]) - 1)*100).toFixed(2)}%)
              </span>
              <span className="base">
                Base value: {baseStats[key]}
                {' '}({(((finalStats[key] / baseStats[key]) - 1) * 100).toFixed(2)}%)
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
